import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendEmailVerification,
  signInWithPopup,
  GoogleAuthProvider,
  sendPasswordResetEmail,
  updateProfile,
  deleteUser,
} from "firebase/auth";
import { app } from "./firebaseConfig";
import {
  getFirestore,
  updateDoc,
  doc,
  getDoc,
  setDoc,
  deleteDoc,
} from "firebase/firestore";
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { v4 as uuidv4 } from "uuid";
import { getErrorMessage } from "utils/firebaseUtility";
import { toast } from "react-toastify";

const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);
const googleProvider = new GoogleAuthProvider();

const firebaseSignUp = async (
  firstName,
  lastName,
  email,
  password,
  privacyPolicy,
  marketing
) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );
    const user = userCredential.user;

    // Update the user's displayName in Firebase
    await updateProfile(user, {
      displayName: `${firstName} ${lastName}`,
    });

    // Add trial information for new users
    const trialStartDate = new Date();
    const trialEndDate = new Date(trialStartDate);
    trialEndDate.setDate(trialEndDate.getDate() + 15); // 15 days trial

    await setDoc(doc(db, "users", user.uid), {
      uid: user.uid,
      firstName,
      lastName,
      email,
      privacyPolicy,
      marketing,
      trialStartDate: trialStartDate.toISOString(),
      trialEndDate: trialEndDate.toISOString(),
    });

    await sendVerificationEmail();

    return await getUserData(user);
  } catch (error) {
    console.log("Signup error", error);
    throw getErrorMessage(error.code);
  }
};

const firebaseSignIn = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );
    const user = userCredential.user;
    if (!user.emailVerified) {
      // Handle email not verified scenario
    }

    return await getUserData(user);
  } catch (error) {
    throw getErrorMessage(error.code);
  }
};

const firebaseSignInWithGoogle = async (privacyPolicy, marketing) => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;

    const userRef = doc(db, "users", user.uid);
    const docSnap = await getDoc(userRef);

    if (!docSnap.exists()) {
      // Add trial information for new Google users
      const trialStartDate = new Date();
      const trialEndDate = new Date(trialStartDate);
      trialEndDate.setDate(trialEndDate.getDate() + 15); // 15 days trial

      const userData = {
        uid: user.uid,
        firstName: user.displayName.split(" ")[0],
        lastName: user.displayName.split(" ")[1],
        email: user.email,
        avatar: user.photoURL,
        trialStartDate: trialStartDate.toISOString(),
        trialEndDate: trialEndDate.toISOString(),
      };

      if (privacyPolicy) {
        userData.privacyPolicy = privacyPolicy;
      }

      if (marketing) {
        userData.marketing = marketing;
      }

      await setDoc(userRef, userData);
    }

    return await getUserData(user);
  } catch (error) {
    console.log("google signin error", error);
    throw getErrorMessage(error.code);
  }
};

const getUserData = async (user) => {
  try {
    const userDoc = await getDoc(doc(db, "users", user.uid));
    if (userDoc.exists()) {
      const userData = userDoc.data();
      return {
        uid: user.uid,
        email: user.email,
        emailVerified: user.emailVerified,
        ...userData,
      };
    } else {
      throw new Error("User document not found");
    }
  } catch (error) {
    throw new Error("Error fetching user data: " + error.message);
  }
};

const sendVerificationEmail = async () => {
  try {
    const user = auth.currentUser;
    await sendEmailVerification(user);
    console.log("Verification email sent.");
  } catch (error) {
    console.log("Send verification emeail error", error);
    throw getErrorMessage(error.code);
  }
};

const firebaseSignOut = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    throw getErrorMessage(error.code);
  }
};

const forgotPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    console.log("Password reset email sent.");
  } catch (error) {
    throw getErrorMessage(error.code);
  }
};

const uploadAvatar = async (file) => {
  const userId = auth.currentUser.uid;
  try {
    const fileRef = ref(storage, `avatars/${userId}/${uuidv4()}-${file.name}`);
    await uploadBytes(fileRef, file);
    const fileUrl = await getDownloadURL(fileRef);

    // Update the user's profile with the new avatar link
    await updateProfile(auth.currentUser, {
      photoURL: fileUrl,
    });

    // Update the user's document with the new avatar link
    const userRef = doc(db, "users", userId);
    await updateDoc(userRef, {
      avatar: fileUrl,
    });

    return fileUrl;
  } catch (error) {
    throw getErrorMessage(error.code);
  }
};

const updateUserInfo = async (firstName, lastName) => {
  try {
    const user = auth.currentUser;

    await updateProfile(user, {
      displayName: `${firstName} ${lastName}`,
    });

    const userRef = doc(db, "users", user.uid);
    await updateDoc(userRef, {
      firstName,
      lastName,
    });
    console.log("User information updated.");
  } catch (error) {
    throw getErrorMessage(error.code);
  }
};

const sendPasswordResetLink = async (email) => {
  const actionCodeSettings = {
    url: `${window.location.origin}/settings`,
    handleCodeInApp: true,
  };

  try {
    await sendPasswordResetEmail(auth, email, actionCodeSettings);
    toast.success("Email Sent!");
  } catch (error) {
    console.error("Error sending password reset email: ", error);
    throw getErrorMessage(error.code);
  }
};

const firebaseDeleteUserAccount = async () => {
  const user = auth.currentUser;

  try {
    await deleteDoc(doc(db, "users", user.uid));

    // const storageRef = ref(storage, `userUploads/${user.uid}`);
    // await deleteObject(storageRef);
    await deleteUser(user);
  } catch (error) {
    console.log("Error deleting user account:", error);
    throw getErrorMessage(error.code);
  }
};

const updateUserAccessInfo = async (uid, userInfo) => {
  try {
    const userDocRef = doc(db, "users", uid);

    await setDoc(
      userDocRef,
      {
        lastAccessTime: userInfo.lastAccessTime,
        device: userInfo.device,
        browser: userInfo.browser,
      },
      { merge: true }
    );
  } catch (error) {
    console.error("Error updating user info:", error);
    throw getErrorMessage(error.code);
  }
};

export {
  auth,
  db,
  firebaseSignUp,
  firebaseSignIn,
  firebaseSignInWithGoogle,
  firebaseSignOut,
  sendVerificationEmail,
  forgotPassword,
  uploadAvatar,
  updateUserInfo,
  sendPasswordResetLink,
  firebaseDeleteUserAccount,
  updateUserAccessInfo,
};
